import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Eye, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney } from "../utils/format";

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", first_name: "", phone: "", whatsapp: "", address: "", email: "", type: "Particulier", profession: "", company: "", id_type: "", id_number: "", birth_date: "", emergency_contact: "", observations: "" });
  const [params] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const delCustomer = async (c) => {
    if (!window.confirm(`Supprimer le client ${c.name} ?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Client supprimé");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = () => api.get("/customers").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    const openId = params.get("open");
    if (openId) openDetail(openId);
  }, []);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/customers/${id}`);
      setDetail(r.data);
    } catch {}
  };

  const submit = async () => {
    if (!form.name) return toast.error("Nom requis");
    try {
      await api.post("/customers", form);
      toast.success("Client créé");
      setOpen(false);
      setForm({ name: "", first_name: "", phone: "", whatsapp: "", address: "", email: "", type: "Particulier", profession: "", company: "", id_type: "", id_number: "", birth_date: "", emergency_contact: "", observations: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const creditBalance = (c) => undefined;

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">Base clients : achats, locations, paiements et crédits.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-customer-button"><Plus className="mr-1 h-4 w-4" /> Nouveau client</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouveau client</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nom *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" /></div>
              <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" /></div>
              <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Date de naissance</Label><Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Particulier", "Entreprise", "Revendeur"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Profession</Label><Input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} data-testid="customer-profession-input" /></div>
              <div><Label>Entreprise</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div><Label>Type de pièce</Label>
                <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
                  <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{["CNI", "Passeport", "Permis de conduire", "Autre"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>N° de pièce d'identité</Label><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} /></div>
              <div><Label>Personne à contacter</Label><Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} /></div>
              <div className="col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="col-span-2"><Label>Observations</Label><Input value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="customer-submit">Créer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="customers-table" rows={rows}
        searchKeys={["name", "phone", "email"]}
        searchPlaceholder="Recherche rapide : nom, téléphone…"
        columns={[
          { key: "name", label: "Nom", render: (r) => <span className="font-semibold text-slate-800">{r.name}</span> },
          { key: "phone", label: "Téléphone" },
          { key: "type", label: "Type" },
          { key: "address", label: "Adresse", render: (r) => <span className="text-slate-500">{r.address || "—"}</span> },
          { key: "total_purchases", label: "Total achats", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.total_purchases)}</span> },
          { key: "total_paid", label: "Total payé", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.total_paid)}</span> },
          { key: "actions", label: "", sortable: false, render: (r) => (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => openDetail(r.id)} data-testid={`view-customer-${r.id}`}><Eye className="h-4 w-4" /></Button>
              {isAdmin && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => delCustomer(r)} data-testid={`delete-customer-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ) },
        ]}
      />

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.customer.name}</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">{detail.customer.phone} · {detail.customer.whatsapp ? `WhatsApp ${detail.customer.whatsapp} · ` : ""}{detail.customer.email || "—"} · {detail.customer.address || "—"}</p>
              {(detail.customer.profession || detail.customer.id_number) && (
                <p className="mt-1 text-xs text-slate-400">
                  {detail.customer.profession && `${detail.customer.profession}${detail.customer.company ? ` — ${detail.customer.company}` : ""} · `}
                  {detail.customer.id_number && `${detail.customer.id_type || "Pièce"} : ${detail.customer.id_number}`}
                </p>
              )}
              <h4 className="font-heading mt-4 text-sm font-semibold">Ventes ({detail.sales.length})</h4>
              <div className="space-y-1">
                {detail.sales.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-mono-num font-semibold">{s.sale_number}</span>
                    <span className="text-slate-500">{s.vehicle_label}</span>
                    <span className="font-mono-num">{fmtMoney(s.final_price)}</span>
                    <StatusBadge status={s.status} />
                  </div>
                ))}
                {detail.sales.length === 0 && <p className="text-sm text-slate-400">Aucune vente.</p>}
              </div>
              <h4 className="font-heading mt-4 text-sm font-semibold">Locations ({detail.rentals.length})</h4>
              <div className="space-y-1">
                {detail.rentals.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-mono-num font-semibold">{r.rental_number}</span>
                    <span className="text-slate-500">{r.vehicle_label}</span>
                    <StatusBadge status={r.status} />
                  </div>
                ))}
                {detail.rentals.length === 0 && <p className="text-sm text-slate-400">Aucune location.</p>}
              </div>
              <h4 className="font-heading mt-4 text-sm font-semibold">Crédits ({detail.credits.length})</h4>
              <div className="space-y-1">
                {detail.credits.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-mono-num font-semibold">{c.sale_number}</span>
                    <span className="text-slate-500">Reste {fmtMoney(c.remaining)}</span>
                    <StatusBadge status={c.status} />
                  </div>
                ))}
                {detail.credits.length === 0 && <p className="text-sm text-slate-400">Aucun crédit.</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
